import React, { FC } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography, Paper } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import CheckboxInput from '@/components/forms/CheckboxInput';
import SelectInput from '@/components/forms/SelectInput';
import { OutlinedContainer } from '@/components/OutlinedContainer';
import TextInput from '@/components/forms/TextInput';
import BannerTitle from './BannerBlockTitle/BannerTitle';
import Container from '../../../../components/Container';
import Styles from '../../../../assets/js/Styles';
import { BannerBlockMediaBackground } from './BannerBlockMediaBackground';
import BannerBlockTextEditor from './BannerBlockTextEditor';
import BannerBlockTextEffects from './BannerBlockTextEffects';
import { BannerBlockAdditionalImages } from './BannerBlockAdditionalImages';
import { useControlledFieldArray } from '../../../../hooks/useControlledFieldArray';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import { BannerBlock as BannerBlockModel } from '../../../../declarations/models/blocks/BannerBlock';
import Links from '../../../../components/Links/Links';
import { LinkType } from '../../../../declarations/models/LinkType';
import { LinkStyle } from '../../../../declarations/models/LinkStyle';
import { useEditorEventHandler, useEditorEventManager } from '../../../lib/eventManager/EditorEventManager';
import { BannerBlockAdvancedSettings } from './BannerBlockAdvancedSettings';
import { BannerIconAndSoundSettings } from './BannerIconAndSoundSettings';

export const BannerBlock: FC = () => {
  const { blockPath } = useCurrentBlock();
  const eventManager = useEditorEventManager();
  const { t: tComponents } = useTranslation('components');
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = React.useState<boolean>(false);
  const effectOptions = ['', 'zoom'];

  const {
    fields: linkItems,
    append,
    update,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<BannerBlockModel>, `${BlockPath}.items`>({
    name: `${blockPath}.items`,
  });

  const mediaObject = useWatch<BlockSpecificPage<BannerBlockModel>, `${BlockPath}.mediaobject`>({
    name: `${blockPath}.mediaobject`,
  });

  return (
    <Container fullWidth column gap={2}>
      <BannerBlockMediaBackground />
      {mediaObject?.type !== 'video' && <BannerBlockAdditionalImages />}
      <BannerTitle />
      <BannerBlockTextEditor />
      <OutlinedContainer fullWidth left label={tComponents('BannerBlock.LinksLabel')} sx={{ mt: 1 }}>
        <Links
          linkTypes={[LinkType.PAGE, LinkType.EVENT, LinkType.DOCUMENT, LinkType.LINK]}
          onUpdate={update}
          onAppend={append}
          onRemove={remove}
          onReorder={move}
          linkItems={linkItems}
          linkStyleTypes={[LinkStyle.BUTTONS, LinkStyle.TEXT_LINKS, LinkStyle.CLICKABLE_MODULE]}
          blockPath={blockPath}
          useEventHandler={useEditorEventHandler}
          useEventManager={eventManager}
        />
      </OutlinedContainer>
      {/* How other links point to the banner, so below the links box, not in it. Same place as in the text module. */}
      <TextInput
        path={`${blockPath}.tocId`}
        label={tComponents('CommonBlockContent.FormLabels.AnchorID')}
        defaultValue=''
      />
      <Container fullWidth left>
        <Accordion
          expanded={advancedSettingsOpen}
          onChange={() => setAdvancedSettingsOpen((o) => !o)}
          elevation={0}
          disableGutters
          sx={{
            width: '100%',
            marginLeft: 0,
          }}>
          <AccordionSummary
            sx={{
              flexDirection: 'row-reverse',
              padding: 0,
            }}
            expandIcon={
              <MaterialSymbol
                name='chevron_right'
                sx={advancedSettingsOpen ? { transform: 'rotate(270deg)' } : undefined}
              />
            }>
            <Typography
              sx={{
                fontWeight: 'bold',
                px: 2,
                '&, &:hover': {
                  textDecoration: 'underline',
                },
              }}>
              {tComponents('BannerBlock.MoreSettingsLabel')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
            }}>
            <Paper>
              <Container
                sx={{ backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR }}
                p={2}
                mt={2}
                mb={4}
                gap={2}
                column
                top
                left
                fullWidth>
                <BannerIconAndSoundSettings path={blockPath} />
                <CheckboxInput
                  fullWidth={false}
                  path={`${blockPath}.nextButton`}
                  label={tComponents('BannerBlock.NextButtonLabel')}
                />
                <CheckboxInput
                  fullWidth={false}
                  path={`${blockPath}.isTransparent`}
                  label={tComponents('BannerBlock.transparent')}
                />
                <Container fullWidth column left gap={0} mt={2}>
                  <SelectInput
                    hideNoSelectionOption
                    defaultValue=''
                    options={effectOptions}
                    getOptionKey={(effect) => `effect-${effect}`}
                    getOptionLabel={(effect) =>
                      `${tComponents(`BannerBlock.AdvancedSettings.effect.options.${effect}`)}`
                    }
                    path={`${blockPath}.effect`}
                    label={tComponents('BannerBlock.AdvancedSettings.effect.Label')}
                  />
                </Container>

                <BannerBlockTextEffects />
                <BannerBlockAdvancedSettings />
              </Container>
            </Paper>
          </AccordionDetails>
        </Accordion>
      </Container>
    </Container>
  );
};

export default BannerBlock;
